import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Lock, User, Baby, ShieldCheck, ChevronLeft } from "lucide-react";
import { PillButton } from "../../components/ui/PillButton";
import { StickerCard } from "../../components/ui/StickerCard";
import { useApp } from "../../context/AppContext";
import { TICKET_PACKAGE } from "../../data/mockData";
import { formatIDR } from "../../lib/format";

const Input = ({ label, required, testid, ...props }) => (
  <label className="block">
    <span className="text-sm font-bold text-sea-deep">{label}{required && <span className="text-coral"> *</span>}</span>
    <input
      data-testid={testid}
      className="mt-1 w-full rounded-2xl border-2 border-slate-200 focus:border-sky-dark focus:ring-4 focus:ring-sky/40 outline-none px-4 py-3 font-semibold text-sea-deep bg-white"
      {...props}
    />
  </label>
);

const Select = ({ label, testid, children, ...props }) => (
  <label className="block">
    <span className="text-sm font-bold text-sea-deep">{label}</span>
    <select
      data-testid={testid}
      className="mt-1 w-full rounded-2xl border-2 border-slate-200 focus:border-sky-dark focus:ring-4 focus:ring-sky/40 outline-none px-4 py-3 font-semibold text-sea-deep bg-white"
      {...props}
    >
      {children}
    </select>
  </label>
);

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { purchaseTicket } = useApp();
  const qty = location.state?.qty || 1;
  const total = qty * TICKET_PACKAGE.price;

  const [buyer, setBuyer] = useState({ fullName: "", email: "", mobile: "" });
  const [participants, setParticipants] = useState(
    Array.from({ length: qty }, () => ({ parentName: "", childName: "", childAge: "", childGender: "", emergencyContact: "", emergencyPhone: "" }))
  );
  const [consent, setConsent] = useState({ tnc: false, waiver: false, photo: true });
  const [submitting, setSubmitting] = useState(false);

  const setP = (i, key, val) => setParticipants((prev) => prev.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));

  const validate = () => {
    if (!buyer.fullName || !buyer.email || !buyer.mobile) return "Lengkapi data pembeli terlebih dahulu.";
    for (let i = 0; i < participants.length; i++) {
      const p = participants[i];
      if (!p.parentName || !p.childName || !p.childAge) return `Lengkapi data peserta paket #${i + 1}.`;
    }
    if (!consent.tnc || !consent.waiver) return "Mohon setujui Terms & Conditions dan waiver partisipasi.";
    return null;
  };

  const handlePay = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    // MOCK PAYMENT — simulate gateway delay
    setTimeout(() => {
      const created = purchaseTicket(buyer, participants);
      setSubmitting(false);
      navigate("/tickets/confirmation", { state: { codes: created.map((c) => c.ticketCode) } });
    }, 900);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sea-deep/60 font-bold mb-4" data-testid="checkout-back">
        <ChevronLeft className="w-4 h-4" /> Kembali
      </button>
      <h1 className="font-display text-3xl sm:text-4xl text-sea-deep">Checkout</h1>
      <p className="text-sea-deep/60 font-semibold">Isi data peserta untuk menyelesaikan pembelian.</p>

      <div className="mt-8 grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          {/* Buyer */}
          <StickerCard className="p-6">
            <h2 className="font-display text-xl text-sea-deep flex items-center gap-2"><User className="w-5 h-5 text-coral" /> Data Pembeli</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Input label="Nama Lengkap" required testid="buyer-fullname" value={buyer.fullName} onChange={(e) => setBuyer({ ...buyer, fullName: e.target.value })} />
              <Input label="Email" type="email" required testid="buyer-email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} />
              <Input label="No. HP / WhatsApp" required testid="buyer-mobile" value={buyer.mobile} onChange={(e) => setBuyer({ ...buyer, mobile: e.target.value })} />
            </div>
          </StickerCard>

          {/* Participants */}
          {participants.map((p, i) => (
            <StickerCard className="p-6" key={i} data-testid={`participant-card-${i}`}>
              <h2 className="font-display text-xl text-sea-deep flex items-center gap-2">
                <Baby className="w-5 h-5 text-coral" /> Paket #{i + 1} — Orang Tua & Anak
              </h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <Input label="Nama Orang Tua" required testid={`p-parent-${i}`} value={p.parentName} onChange={(e) => setP(i, "parentName", e.target.value)} />
                <Input label="Nama Anak" required testid={`p-child-${i}`} value={p.childName} onChange={(e) => setP(i, "childName", e.target.value)} />
                <Input label="Usia Anak" type="number" min="0" max="17" required testid={`p-age-${i}`} value={p.childAge} onChange={(e) => setP(i, "childAge", e.target.value)} />
                <Select label="Gender Anak (opsional)" testid={`p-gender-${i}`} value={p.childGender} onChange={(e) => setP(i, "childGender", e.target.value)}>
                  <option value="">-</option>
                  <option>Laki-laki</option>
                  <option>Perempuan</option>
                </Select>
                <Input label="Kontak Darurat" testid={`p-emg-${i}`} value={p.emergencyContact} onChange={(e) => setP(i, "emergencyContact", e.target.value)} />
                <Input label="No. Kontak Darurat" testid={`p-emgphone-${i}`} value={p.emergencyPhone} onChange={(e) => setP(i, "emergencyPhone", e.target.value)} />
              </div>
            </StickerCard>
          ))}

          {/* Consent */}
          <StickerCard className="p-6">
            <h2 className="font-display text-xl text-sea-deep flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-coral" /> Persetujuan</h2>
            <div className="mt-3 space-y-3">
              {[
                { k: "tnc", label: "Saya menyetujui Terms & Conditions" },
                { k: "waiver", label: "Saya menyetujui waiver partisipasi event" },
                { k: "photo", label: "Saya mengizinkan dokumentasi / fotografi event" },
              ].map((c) => (
                <label key={c.k} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent[c.k]}
                    onChange={(e) => setConsent({ ...consent, [c.k]: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded accent-coral"
                    data-testid={`consent-${c.k}`}
                  />
                  <span className="text-sm font-semibold text-sea-deep">{c.label}</span>
                </label>
              ))}
            </div>
          </StickerCard>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24">
          <StickerCard className="p-6" data-testid="checkout-summary">
            <h3 className="font-display text-xl text-sea-deep">Ringkasan</h3>
            <div className="mt-4 flex items-center justify-between text-sm font-bold text-sea-deep/80">
              <span>Parent + Kiddo × {qty}</span>
              <span>{formatIDR(total)}</span>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="font-bold text-sea-deep/70">Total</span>
              <span className="font-display text-2xl text-coral">{formatIDR(total)}</span>
            </div>

            <div className="mt-4 bg-sky/15 rounded-2xl p-3 text-xs font-semibold text-sea-deep/70 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-sky-dark" />
              Pembayaran <b>simulasi (MOCK)</b> — siap diintegrasikan gateway asli nanti.
            </div>

            <PillButton
              size="lg"
              className="w-full mt-4"
              onClick={handlePay}
              disabled={submitting}
              data-testid="pay-now"
            >
              <Lock className="w-4 h-4" /> {submitting ? "Memproses..." : `Bayar ${formatIDR(total)}`}
            </PillButton>
          </StickerCard>
        </div>
      </div>
    </div>
  );
}
